/**
 * The latest AI run's metadata, parsed from the response's additionalAttributes: the decision
 * and its confidence, why it was not auto-sent, the detected intents and what the answer drew
 * on, with workflow ids linking to their editors. The body of the sidebar's Run Details
 * section, so it owns no card chrome.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Sparkles, ShieldAlert, CircleAlert, ExternalLink } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { humanize } from '../ui/text';
import { SectionEmpty } from './panel';
import type { RunDetails, RunApprovalTool } from '@/stores/websocket.store';

const pct = (v?: number): string => (typeof v === 'number' ? `${Math.round(v * 100)}%` : '—');

const confidenceTone = (v?: number): string => {
  if (typeof v !== 'number') return 'text-gray-800';
  if (v >= 0.8) return 'text-emerald-600';
  if (v >= 0.5) return 'text-amber-600';
  return 'text-red-600';
};

// Recolours the vendored <Progress> indicator via its data-slot, on the same thresholds as the text.
const barIndicator = (v?: number): string => {
  if (typeof v !== 'number') return '**:data-[slot=progress-indicator]:bg-gray-800';
  if (v >= 0.8) return '**:data-[slot=progress-indicator]:bg-emerald-500';
  if (v >= 0.5) return '**:data-[slot=progress-indicator]:bg-amber-500';
  return '**:data-[slot=progress-indicator]:bg-red-500';
};

const ROUTING_LABEL: Record<string, string> = {
  auto_reply: 'Auto reply',
  awaiting_supervisor: 'Awaiting supervisor',
  human_handoff: 'Human handoff',
  escalated: 'Escalated',
  no_information: 'No information',
  kb: 'Knowledge base',
  tool_workflow: 'Tool workflow',
};

const routingTone = (r?: string): string => {
  switch (r) {
    case 'auto_reply':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'awaiting_supervisor':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'human_handoff':
    case 'escalated':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-text-body border-gray-500';
  }
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className='flex flex-col gap-1.5'>
    <span className='text-xs leading-4 font-medium text-gray-800'>{title}</span>
    {children}
  </div>
);

const ConfidenceMeter = ({ value }: { value: number }) => (
  <div className='flex items-center gap-2'>
    <span className='shrink-0 text-xs text-gray-800'>Confidence</span>
    <Progress value={Math.round(value * 100)} className={cn('h-1.5 flex-1 bg-gray-300', barIndicator(value))} />
    <span className={cn('shrink-0 text-xs font-medium tabular-nums', confidenceTone(value))}>{pct(value)}</span>
  </div>
);

/** One grounding source: its label and count, then its items as text or as deep-link chips. */
const GroundRow = ({
  label,
  items,
  hrefFor,
  trailing,
}: {
  label: string;
  items: string[];
  hrefFor?: (id: string) => string;
  trailing?: ReactNode;
}) => (
  <div className='flex flex-col gap-1'>
    <span className='text-xs text-gray-800'>
      {label} · {items.length}
    </span>
    {hrefFor ? (
      <div className='flex flex-wrap gap-1'>
        {items.map((id) => (
          <a
            key={id}
            href={hrefFor(id)}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-1 rounded-full border border-gray-500 bg-white px-2 font-mono text-[11px] leading-5 text-text-body transition-colors hover:bg-gray-100'
          >
            {id.slice(0, 8)}…
            <ExternalLink className='h-2.5 w-2.5' />
          </a>
        ))}
        {trailing}
      </div>
    ) : (
      <span className='text-[13px] leading-4.5 wrap-break-word text-text-body'>{items.join(', ')}</span>
    )}
  </div>
);

const TraceLink = ({ href }: { href: string }) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='inline-flex items-center gap-1 rounded-full border border-gray-500 bg-white px-2 text-[11px] leading-5 font-medium text-text-body transition-colors hover:bg-gray-100'
  >
    Trace
    <ExternalLink className='h-2.5 w-2.5' />
  </a>
);

// While a run is in flight: the section scaffold with its values still loading,
// so the panel visibly fills in (details arrive all at once at the end).
const RunDetailsLoading = () => (
  <div className='flex flex-col gap-4'>
    <Section title='Decision'>
      <Skeleton className='h-5 w-24 rounded-full' />
    </Section>
    <Section title='Intents'>
      <Skeleton className='h-4 w-full rounded' />
      <Skeleton className='h-4 w-2/3 rounded' />
    </Section>
    <Skeleton className='h-4 w-1/2 rounded' />
  </div>
);

// When a run fails (an `error` event), so the rail matches the thread's error bubble.
const RunDetailsError = ({ message }: { message: string }) => (
  <div className='flex flex-col items-center gap-2 py-3 text-center'>
    <div className='flex h-9 w-9 items-center justify-center rounded-full bg-red-50'>
      <CircleAlert className='h-4 w-4 text-red-600' />
    </div>
    <p className='text-[13px] font-medium text-red-700'>Run failed</p>
    <p className='max-w-[220px] text-xs text-gray-800'>{message}</p>
  </div>
);

// When the run paused for human approval of its writes (HUMAN_APPROVAL_REQUEST)
// instead of replying; the writes did not execute.
const RunDetailsAwaiting = ({ tools }: { tools: RunApprovalTool[] }) => (
  <div className='flex flex-col gap-3'>
    <div className='flex items-center gap-2'>
      <ShieldAlert className='h-4 w-4 shrink-0 text-amber-600' />
      <p className='text-[13px] font-medium text-amber-700'>Awaiting human approval; nothing was executed</p>
    </div>
    <div className='flex flex-col gap-1.5'>
      {tools.map((t, i) => (
        <div key={`${t.toolName}-${i}`} className='rounded-lg border border-amber-200 bg-amber-50/40 px-2.5 py-2'>
          <span className='font-mono text-xs font-medium text-text-body'>{t.toolName}</span>
          {t.reason && <p className='mt-0.5 text-xs text-gray-800'>{t.reason}</p>}
        </div>
      ))}
    </div>
  </div>
);

interface RunDetailsContentProps {
  details: RunDetails | null;
  loading?: boolean;
  error?: string | null;
  approval?: RunApprovalTool[] | null;
}

export const RunDetailsContent = ({ details, loading, error, approval }: RunDetailsContentProps) => {
  if (loading) return <RunDetailsLoading />;
  const ending = error ? (
    <RunDetailsError message={error} />
  ) : approval?.length ? (
    <RunDetailsAwaiting tools={approval} />
  ) : null;
  if (!details) return ending ?? <SectionEmpty icon={Sparkles}>Ask AI to see how it decided.</SectionEmpty>;

  return (
    <div className='flex flex-col gap-4'>
      {ending}
      <RunDetailsBody details={details} />
    </div>
  );
};

/** What the run reported, as far as it got: a failed or paused run may carry only its trace. */
const RunDetailsBody = ({ details }: { details: RunDetails }) => {
  const hasDecision =
    Boolean(details.routing) || typeof details.confidence === 'number' || Boolean(details.automationBlockReason);
  const hasGrounding =
    details.kbIds.length > 0 ||
    details.segIds.length > 0 ||
    details.ruleNames.length > 0 ||
    details.guardrails.length > 0 ||
    details.similarTicketIds.length > 0;
  const trace = details.traceUrl ? <TraceLink href={details.traceUrl} /> : null;
  const hasProtocols = details.segIds.length > 0;

  return (
    <div className='flex flex-col gap-4'>
      {hasDecision && (
        <Section title='Decision'>
          {details.routing && (
            <span
              className={cn(
                'w-fit rounded-full border px-2 text-xs leading-5 font-medium',
                routingTone(details.routing)
              )}
            >
              {ROUTING_LABEL[details.routing] ?? humanize(details.routing)}
            </span>
          )}
          {typeof details.confidence === 'number' && <ConfidenceMeter value={details.confidence} />}
          {details.automationBlockReason && (
            <p className='text-xs text-amber-700'>Held from auto-send: {humanize(details.automationBlockReason)}</p>
          )}
        </Section>
      )}

      {details.intents.length > 0 && (
        <Section title='Intents'>
          {details.intents.map((it, i) => {
            const isFinal = details.finalIntent.includes(it.type);
            return (
              <div key={`${it.type}-${i}`} className='flex items-center justify-between gap-2'>
                <span className={cn('min-w-0 truncate text-[13px] leading-4.5', isFinal ? 'font-medium text-text-body' : 'text-gray-800')}>
                  {humanize(it.type)}
                </span>
                {typeof it.confidence === 'number' && (
                  <span className={cn('shrink-0 text-xs tabular-nums', confidenceTone(it.confidence))}>
                    {pct(it.confidence)}
                  </span>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {hasGrounding && (
        <Section title='Grounding'>
          <div className='flex flex-col gap-2'>
            {/* No knowledge-base detail page exists, so these stay plain ids. */}
            {details.kbIds.length > 0 && <GroundRow label='Knowledge bases' items={details.kbIds} />}
            {hasProtocols && (
              // `v3_seg_ids` carries workflow ids, not seg ids; the editor route is what resolves them.
              <GroundRow
                label='Protocol'
                items={details.segIds}
                hrefFor={(id) => `/training/workflows/v2/${id}`}
                trailing={trace}
              />
            )}
            {details.ruleNames.length > 0 && <GroundRow label='Rules' items={details.ruleNames} />}
            {details.guardrails.length > 0 && <GroundRow label='Guardrails' items={details.guardrails} />}
            {details.similarTicketIds.length > 0 && <GroundRow label='Similar tickets' items={details.similarTicketIds} />}
          </div>
        </Section>
      )}

      {/* With no protocol to sit beside, the trace keeps a row of its own. */}
      {trace && !hasProtocols && <div className='flex justify-end'>{trace}</div>}
    </div>
  );
};
